import {IPatientGoalSearch} from './patient-goal-search.interface';
import {IPatientGoal} from './patient-goal.interface';
import {cloneDeep} from 'lodash';
import {BaseService, DataBaseServiceResponse} from '../../services/base-service/base.service';
import {firstValueFrom} from 'rxjs';
import {IPostPatientGoal} from './post-patient-goal.interface';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import {StorageService} from '../../services/storage/storage.service';

export interface IRPatientGoal {
  patientGoals: IPatientGoal[];
  patientGoalSelectedId?: string;
  count?: number;
  optionsSearch: IPatientGoalSearch[];
}

export namespace PatientGoalAction {
  export class Get {
    static readonly type = '[PATIENT GOAL] Get Patient Goals Action';
    constructor(public serviceId: string, public patientId: string, public filters?: {
      params: {
        skip: number,
        limit: number
      }
    }) { }
  }
  export class Post {
    static readonly type = '[PATIENT GOAL] Post Patient Goals Action';
    constructor(public serviceId: string, public patientId: string, public patientGoal: IPostPatientGoal) { }
  }

  export class Count {
    static readonly type = '[PATIENT GOAL] Get Patient Goals Count Action';
    constructor(public serviceId: string, public patientId: string, public filter: any) { }
  }

  export class DeleteById {
    static readonly type = '[PATIENT GOAL] Delete Patient Goals Action';
    constructor(public serviceId: string, public patientId: string, public id: string, public itemId: string) { }
  }

  export class DeleteAll {
    static readonly type = '[PATIENT GOAL] Delete Patient Goals Complete Action';
    constructor(public serviceId: string, public patientId: string, public goalId: string) { }
  }

  export class SearchPatientGoals {
    static readonly type = '[PATIENT GOAL] Get Patient Goals Search Action';
    constructor(public serviceId: string, public patientId: string) { }
  }
}

@State<IRPatientGoal>({
  name: 'PatientGoalState',
  defaults: {
    patientGoals: [],
    patientGoalSelectedId: null,
    optionsSearch: []
  }
})

@Injectable()
export class PatientGoalState {
  constructor(
    private storageService: StorageService,
    private baseService: BaseService<any>
  ) {
  }
  url = environment.SERVER_URL;

  @Selector()
  public static getPatientGoals(state: IRPatientGoal): IPatientGoal[] {
    return state.patientGoals;
  }

  @Selector()
  public static getPatientGoalsCount(state: IRPatientGoal): number {
    return state.count;
  }

  @Selector()
  public static getPatientGoalsOptionsSearch(state: IRPatientGoal): IPatientGoalSearch[] {
    return state.optionsSearch;
  }

  @Action(PatientGoalAction.Get)
  async getPatientGoalsAction(ctx: StateContext<IRPatientGoal>, action: PatientGoalAction.Get): Promise<void> {
    const response: DataBaseServiceResponse<IPatientGoal[]> = await firstValueFrom(
      this.baseService.get(`${this.url}/services/${action.serviceId}/patients/${action.patientId}/patient-goals`,  void 0, void 0, {
        params: {
          ...action.filters?.params ?? {},
        }
      })
    );
    if (response.error) throw new Error(response.message);

    ctx.patchState({patientGoals: response.entity});
  }

  @Action(PatientGoalAction.SearchPatientGoals)
  async getPatientGoalsSearchAction(ctx: StateContext<IRPatientGoal>, action: PatientGoalAction.SearchPatientGoals): Promise<void> {
    const response: DataBaseServiceResponse<IPatientGoalSearch[]> = await firstValueFrom(
      this.baseService.get(`${this.url}/services/${action.serviceId}/patients/${action.patientId}/patient-goals/search`)
    );
    if (response.error) throw new Error(response.message);

    ctx.patchState({optionsSearch: response.entity});
  }

  @Action(PatientGoalAction.Post)
  async postPatientGoalsAction(ctx: StateContext<IRPatientGoal>, action: PatientGoalAction.Post): Promise<void> {
    const response: DataBaseServiceResponse<IPatientGoal> = await firstValueFrom(
      this.baseService.post(`${this.url}/services/${action.serviceId}/patients/${action.patientId}/patient-goals`, action.patientGoal)
    );

    if (response.error) throw new Error(response.message);


    const goals: IPatientGoal[] = ctx.getState()?.patientGoals || [];
    // buscar si ya existe uno en los goals
    const index = goals.findIndex(goal => goal.measurementId === response.entity.measurementId);
    if (index !== -1) {
      goals[index].active = false;
      goals.splice(index, 0, response.entity);
    } else {
      goals.unshift(response.entity);
    }

    ctx.patchState({
      patientGoals: goals,
      count: (ctx.getState()?.count || 0) + 1
    });
  }

  @Action(PatientGoalAction.DeleteById)
  async deletePatientGoalsAction(ctx: StateContext<IRPatientGoal>, {serviceId, patientId, id, itemId}: PatientGoalAction.DeleteById): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.delete(`${this.url}/services/${serviceId}/patients/${patientId}/patient-goals/${id}/items/${itemId}`)
    );
    if (response.error) throw new Error(response.message);
  }

  @Action(PatientGoalAction.DeleteAll)
  async deletePatientGoalsCompleteAction(ctx: StateContext<IRPatientGoal>, action: PatientGoalAction.DeleteAll): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.delete(`${this.url}/services/${action.serviceId}/patients/${action.patientId}/patient-goals/${action.goalId}`)
    );

    if (response.error) throw new Error(response.message);

    const goals: IPatientGoal[] = cloneDeep(ctx.getState().patientGoals);
    const index: number = goals.findIndex((item: IPatientGoal) => item._id === action.goalId);
    if (index !== -1) {
      goals.splice(index, 1);

      ctx.patchState({
        patientGoals: goals,
        count: ctx.getState().count - 1
      });
    }
  }

  @Action(PatientGoalAction.Count)
  async getPatientGoalsCountAction(ctx: StateContext<IRPatientGoal>, action?: PatientGoalAction.Count): Promise<void> {
    const response: DataBaseServiceResponse<{count: number}> = await firstValueFrom(
      this.baseService.get(`${this.url}/services/${action.serviceId}/patients/${action.patientId}/patient-goals/count`, void 0, void 0, {
        params: {
          ...action.filter
        }
      })
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      count: response.entity.count,
    });
  }
}
